/**
 * AuthComponents.jsx
 * Shared form atoms used by Login, Register, ForgotPassword, etc.
 * No layout. No LeftPanel. Just components.
 */

import { useState } from "react";
import { Eye, EyeOff, Lock, ArrowLeft, Check, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";

/* ── Inject keyframe animations once ─────────────────────────────── */
if (typeof document !== "undefined" && !document.getElementById("auth-kf")) {
  const s = document.createElement("style");
  s.id = "auth-kf";
  s.textContent = `
    @keyframes auth-floatY    { 0%,100%{transform:translateY(0)}  50%{transform:translateY(-7px)} }
    @keyframes auth-floatSlow { 0%,100%{transform:translateY(0)}  50%{transform:translateY(-4px)} }
    @keyframes auth-spin      { to{transform:rotate(360deg)} }
    @keyframes auth-shk       { 0%,100%{transform:translateX(0)} 25%,75%{transform:translateX(-3px)} 50%{transform:translateX(3px)} }
  `;
  document.head.appendChild(s);
}

/* ── Shared tokens ────────────────────────────────────────────────── */
export const T = {
  fontDisplay: "'DM Sans', sans-serif",
  fontBody:    "'DM Sans', sans-serif",
  fontMono: "'DM Sans', sans-serif",
  amber:       "#111111",
  amberDark:   "#333333",
  text:        "#111827",
  textMid:     "#374151",
  textSoft:    "#6b7280",
  textFaint:   "#9ca3af",
  border:      "#dde1e9",
  inputBg:     "#f8f9fb",
  white:       "#ffffff",
};

const inputBase = {
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  background: T.inputBg,
  border: `1px solid ${T.border}`,
  borderRadius: 10,
  padding: "11px 13px",
  fontFamily: T.fontBody,
  fontSize: "0.875rem",
  color: T.text,
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
  WebkitAppearance: "none",
  appearance: "none",
};

const labelStyle = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  fontFamily: T.fontBody,
  fontSize: "0.67rem",
  fontWeight: 500,
  letterSpacing: "0.04em",
  color: T.textSoft,
  marginBottom: 6,
};

/* ── FField ───────────────────────────────────────────────────────── */
export function FField({ label, icon: Icon, type = "text", value, onChange, placeholder, disabled, required, status }) {
  const [show, setShow] = useState(false);
  const isPw = type === "password";

  const borderColor = status === "ok" ? "#22c55e" : status === "er" ? "#ef4444" : T.border;
  const focusShadow = status === "ok"
    ? "0 0 0 3px rgba(34,197,94,0.12)"
    : status === "er"
      ? "0 0 0 3px rgba(239,68,68,0.12)"
      : "0 0 0 3px rgba(0,0,0,0.10)";

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={labelStyle}>
        {Icon && <Icon size={11} style={{ color: T.textFaint }} />}
        {label}
        {required && <span style={{ color: T.amber }}>*</span>}
        {disabled && (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 4,
            padding: "1px 6px", background: "#f3f4f6", border: "1px solid #e5e7eb",
            borderRadius: 4, fontSize: "0.54rem", color: T.textFaint,
          }}>
            <Lock size={8} /> Locked
          </span>
        )}
      </div>
      <div style={{ position: "relative" }}>
        {Icon && (
          <span style={{
            position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            color: T.textFaint, display: "flex", pointerEvents: "none",
          }}>
            <Icon size={13} />
          </span>
        )}
        <input
          type={isPw ? (show ? "text" : "password") : type}
          value={value}
          onChange={e => onChange?.(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={isPw ? "current-password" : undefined}
          onFocus={e => { e.target.style.borderColor = status ? borderColor : T.amber; e.target.style.boxShadow = focusShadow; }}
          onBlur={e => { e.target.style.borderColor = borderColor; e.target.style.boxShadow = "none"; }}
          style={{
            ...inputBase,
            borderColor,
            paddingLeft: Icon ? 36 : 13,
            paddingRight: isPw ? 38 : 13,
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? "not-allowed" : "text",
          }}
        />
        {isPw && (
          <button type="button" onClick={() => setShow(s => !s)} style={{
            position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer",
            color: T.textFaint, display: "flex", padding: 3,
          }}>
            {show ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}

/* ── SelField ─────────────────────────────────────────────────────── */
export function SelField({ label, icon: Icon, value, onChange, options, required, placeholder = "Select…" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={labelStyle}>
        {Icon && <Icon size={11} style={{ color: T.textFaint }} />}
        {label}
        {required && <span style={{ color: T.amber }}>*</span>}
      </div>
      <div style={{ position: "relative" }}>
        {Icon && (
          <span style={{
            position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            color: T.textFaint, display: "flex", pointerEvents: "none",
          }}>
            <Icon size={13} />
          </span>
        )}
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={e => { e.target.style.borderColor = T.amber; e.target.style.boxShadow = "0 0 0 3px rgba(0,0,0,0.10)"; }}
          onBlur={e => { e.target.style.borderColor = T.border; e.target.style.boxShadow = "none"; }}
          style={{ ...inputBase, paddingLeft: Icon ? 36 : 13, cursor: "pointer" }}
        >
          <option value="">{placeholder}</option>
          {options.map(o => <option key={o}>{o}</option>)}
        </select>
      </div>
    </div>
  );
}

/* ── ProgDots ─────────────────────────────────────────────────────── */
export function ProgDots({ steps, cur }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 20 }}>
      {steps.map((s, i) => (
        <span key={s} style={{ display: "contents" }}>
          <div title={s} style={{
            borderRadius: 9999, transition: "all 0.25s",
            width: i < cur ? 16 : 8, height: 8,
            background: i <= cur ? T.amber : "#e5e7eb",
            boxShadow: i === cur ? "0 0 0 3px rgba(0,0,0,0.15)" : "none",
          }} />
          {i < steps.length - 1 && (
            <div style={{ flex: 1, maxWidth: 22, height: 1, background: i < cur ? T.amber : "#e5e7eb", transition: "background 0.25s" }} />
          )}
        </span>
      ))}
    </div>
  );
}

/* ── StrBar ───────────────────────────────────────────────────────── */
export function StrBar({ score }) {
  const colors = ["#ef4444", "#f97316", "#eab308", "#84cc16", "#22c55e"];
  return (
    <div style={{ display: "flex", gap: 3, marginTop: 6 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} style={{
          flex: 1, height: 3, borderRadius: 9999,
          background: i <= score ? colors[i - 1] : "#e5e7eb",
          transition: "background 0.25s",
        }} />
      ))}
    </div>
  );
}

/* ── ErrBox ───────────────────────────────────────────────────────── */
export function ErrBox({ msg }) {
  if (!msg) return null;
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 8,
      background: "rgba(239,68,68,0.05)",
      border: "1px solid rgba(239,68,68,0.2)",
      borderRadius: 10, padding: "10px 12px",
      color: "#dc2626", fontSize: "0.8rem",
      lineHeight: 1.5, marginBottom: 14,
      fontFamily: T.fontBody,
      animation: "auth-shk 0.3s ease",
    }}>
      <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{msg}</span>
    </div>
  );
}

/* ── BackBtn ──────────────────────────────────────────────────────── */
export function BackBtn({ onClick, children = "Back" }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: "none", border: "none", cursor: "pointer",
      fontFamily: T.fontBody, fontSize: "0.78rem", color: T.textFaint,
      padding: 0, marginBottom: 20, transition: "color 0.15s",
    }}
      onMouseEnter={e => e.currentTarget.style.color = T.textMid}
      onMouseLeave={e => e.currentTarget.style.color = T.textFaint}
    >
      <ArrowLeft size={13} />{children}
    </button>
  );
}

/* ── Spinner ──────────────────────────────────────────────────────── */
export function Spinner() {
  return (
    <span style={{
      display: "inline-block", width: 13, height: 13, borderRadius: "50%",
      border: "2px solid rgba(0,0,0,0.15)", borderTopColor: "#111827",
      animation: "auth-spin 0.75s linear infinite", flexShrink: 0,
    }} />
  );
}

/* ── BtnPrimary ───────────────────────────────────────────────────── */
export function BtnPrimary({ children, onClick, disabled, type = "button", style: extra = {} }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
      width: "100%", padding: "13px 22px",
      background: disabled ? "#e5e7eb" : "#111111",
      color: disabled ? T.textFaint : "#ffffff",
      border: "none", borderRadius: 10,
      fontFamily: T.fontBody, fontWeight: 600, fontSize: "0.9rem",
      cursor: disabled ? "not-allowed" : "pointer",
      boxShadow: "none",
      transition: "all 0.15s",
      ...extra,
    }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.background = "#333333"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
      onMouseLeave={e => { if (!disabled) { e.currentTarget.style.background = "#111111"; e.currentTarget.style.transform = "none"; } }}
    >
      {children}
    </button>
  );
}

/* ── AuthLink ─────────────────────────────────────────────────────── */
export function AuthLink({ children, to, href }) {
  const s = { color: T.amberDark, textDecoration: "none", fontWeight: 500 };
  if (to)   return <Link to={to} style={s}>{children}</Link>;
  if (href) return <a href={href} style={s}>{children}</a>;
  return <span style={s}>{children}</span>;
}

/* ── LogoZone — for single-page card flows ────────────────────────── */
export function LogoZone({ publicBrand }) {
  const [imgErr, setImgErr] = useState(false);
  const isFilled = !!publicBrand?.logoUrl;
  const logoImg = isFilled
    ? <img src={publicBrand.logoUrl} alt={publicBrand.companyName || "Logo"} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
    : imgErr
      ? <span style={{ fontFamily: T.fontBody, fontSize: "0.8rem", color: T.text }}>MDP</span>
      : <img src="/assets/project_logo.png" alt="MyDesignerPlus" style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={() => setImgErr(true)} />;

  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
      marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #edf0f4",
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 8, flexShrink: 0,
        background: "#f0f2f5", overflow: "hidden",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {logoImg}
      </div>
      <span style={{ fontFamily: T.fontBody, fontWeight: 700, fontSize: "0.95rem", color: T.text }}>
        {isFilled ? publicBrand.companyName : <>MyDesignerPlus</>}
      </span>
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────────────────── */
export function pwStrength(pw) {
  if (!pw) return { score: 0, label: "" };
  let s = 0;
  if (pw.length >= 8) s++;
  if (/[a-z]/.test(pw)) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(pw)) s++;
  return { score: s, label: ["Very Weak","Weak","Fair","Good","Strong"][Math.min(s, 4)] };
}

export function generatePassword() {
  const L = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz", D = "23456789", S = "!@#$%^&*";
  const all = L + D + S, pick = c => c[Math.floor(Math.random() * c.length)];
  let p = pick(L.toUpperCase()) + pick(L.toLowerCase()) + pick(D) + pick(S);
  while (p.length < 14) p += pick(all);
  return p.split("").sort(() => Math.random() - 0.5).join("");
}

export const COUNTRIES = [
  "United States","Canada","United Kingdom","Australia","New Zealand","Ireland",
  "Germany","France","Italy","Spain","Netherlands","Sweden","Norway","Denmark",
  "Finland","Switzerland","Austria","Belgium","Poland","Portugal","Mexico",
  "Brazil","Japan","South Korea","India","Singapore","UAE","South Africa","Other",
];

export const ROLES = ["Homeowner","Architect","Engineer","Builder","Contractor","Developer","Student","Other"];
